// Angular
import { Component, ElementRef, ViewChild, AfterViewInit, HostListener, Renderer2 } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

// Rx
import { Subject, Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// Models
import { UserProfile, CaseDetailsProperty } from 'app/_models';
import { CaseShort } from 'app/_models';
import { Theme } from 'app/_models';

// Services
import { SettingsSingletonService } from 'app/_services/settingssingleton.service';
import { ThemeService } from 'app/_components/theme/theme.service';
import { CaseService, WISEService } from 'app/_services';

// Environment
import { environment } from 'environments/environment';

// Common
import { SharedService } from 'app/_services';

// ngx-translate
import { TranslateService, LangChangeEvent, DefaultLangChangeEvent } from '@ngx-translate/core';

// 3rd party
import { NgForage, Driver, NgForageCache, NgForageConfig, CachedItem } from 'ngforage';
import { AuthenticationService } from 'app/_services';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngxs/store';
import { ResetState } from 'app/_actions/intake.actions';
import { CedNotificationService } from 'ced-ng-library';
import { DialogService } from '@progress/kendo-angular-dialog';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';

@Component({
  selector: 'app-dashboard',
  templateUrl: './full-layout.component.html',
  styleUrls: ['full-layout.component.scss'],
  providers: [
    NgForage,
    NgForageCache
  ]
})
export class FullLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('viewheader', { static: false }) headerView: ElementRef;
  private _subject$: Subject<boolean> = new Subject();
  private subscriptions: Subscription[] = [];

  public eProductType = environment.ProductType;
  public disabled = false;
  public status: { isopen: boolean } = { isopen: false };
  public profile: UserProfile = new UserProfile();

  public themeData: Theme = new Theme();

  public hasCase: boolean = false;
  public currentCase: CaseShort = new CaseShort();
  public envName: string;
  public productVersion: string;
  public isLoading: boolean = false;
  public listLanguage: Array<{ text: string, value: string }> = [
    { text: 'DUTCH', value: 'nl' },
    { text: 'ENGLISH', value: 'en' }
  ];
  dossierFilter: string;
  public state: State = {
    skip: 0,
    take: 20,
    filter: {
      logic: "and",
      filters: [],
    },
    sort: [
      {
        field: null,
        dir: null,
      },
    ],
  };
  public currentLang;


  constructor(
    private store: Store,
    private router: Router,
    private ngf: NgForage,
    private themeService: ThemeService,
    public translate: TranslateService,
    private el: ElementRef,
    private caseService: CaseService,
    private authenticationService: AuthenticationService,
    private wiseService: WISEService,
    private sharedService: SharedService,
    private dialogService: DialogService,
    private cedNotificationService: CedNotificationService,
  ) {
    this.setLanguage();

    this.subscriptions.push(
      fromEvent(window, 'resize')
        .pipe(debounceTime(250))
        .subscribe((event) => {
          this.onResize(event);
        }));

    this.setProfile();
    this.envName = environment.envName;
    this.productVersion = environment.productVersion;
    
  }

  public showMenu(): boolean {
    if (environment.ProductType === 'DEV') {
      return true;
    }

    return false;
  }

  public showThemeBreadCrumbs(): boolean {
    return this.themeData.showBreadCrumbs;
  }

  public ChangeRoute(routeValue): void {

    if (this.router.url !== routeValue) {
      setTimeout(() => {
        this.router.navigate([routeValue]);
      }, 500);
    }
  }

  private inflate() {

    if (typeof (Event) === 'function') {
      this.onResize(new Event('resize'));
    } else {
      const event = document.createEvent('Event');

      event.initEvent('resize', true, true);
      this.onResize(event);
    }
  }

  setProfile() {
    let _currentUser = this.authenticationService.currentUserValue;
    this.profile.fullName = _currentUser.name;
    this.profile.emailAddress = _currentUser.unique_name;
    this.profile.familyName = _currentUser.family_name;
    this.profile.givenName = _currentUser.given_name;
    this.profile.ipAddress = _currentUser.ipaddr;
  }

  public logOutUser() {
    this.authenticationService.logout();

    this.router.navigate(['/pages/login']);
  }

  private onResize(event) {

    const innerWindowHeight = window.innerHeight;
    const innerWindowWidth = window.innerWidth;
    const headerHeight = 45;

    SettingsSingletonService.getInstance().setRect(innerWindowWidth, innerWindowHeight - headerHeight);
  }

  ngOnInit() {
    this.subscriptions.push(this.translate.onDefaultLangChange.subscribe((event: DefaultLangChangeEvent) => {
      // do something
    }));

    this.subscriptions.push(this.caseService.getCase().subscribe(params => {
      if (params['CaseID'] && params['InsuredPersonName'] && params['CaseDate']) {
        this.currentCase.CaseID = params['CaseID'].toString();
        this.currentCase.CaseDate = params['CaseDate'];
        this.currentCase.InsuredPersonName = params['InsuredPersonName'];

        this.hasCase = true;
      } else if (params['CaseID']) {
        this.wiseService.getCaseDetailsProperty(params['CaseID']).subscribe((data: CaseDetailsProperty) => {
          if (data && data.Case !== null) {

            this.currentCase.CaseID = data.HeaderDetails.CaseID.toString();
            this.currentCase.CaseDate = data.HeaderDetails.CaseDate;
            this.currentCase.InsuredPersonName = data.HeaderDetails.InsuredPersonName;

            this.hasCase = true;
          }
        });
      } else {
        this.currentCase.resetCase();
        this.hasCase = false;
      }
    },
    (error)=> {
      this.cedNotificationService.warning("NOTIFICATIONS.GET_CASE");
    }));

    this.subscriptions.push(this.sharedService.changeEmitted$.subscribe(data => {
      if (data) {
        if (data.action === 'preloader') {
          this.isLoading = data.value;
        }
      }
    },
    (error)=> {
      this.cedNotificationService.warning("NOTIFICATIONS.GET_CASE");
    }));

    this.ngf.getItem<Theme>('theme')
      .then(data => {
        if (data) {
          this.themeData = data;

          this.themeService.setActiveTheme(this.themeData.themeProps);
        } else {
          this.ngf.setItem('theme', this.themeData);
        }
      });
  }

  ngAfterViewInit() {
    $.getScript('assets/js/main.js', function () {
    });
    
    setTimeout(_ => this.inflate());
  }

  setLanguage(): void {
    let currentLang = (this.translate.currentLang) ? this.translate.currentLang : this.translate.defaultLang;
    this.currentLang = this.listLanguage[this.listLanguage.findIndex(l => l.value == currentLang)];
  }

  changeLang(lang): void {
    event.preventDefault(); 
    this.currentLang = lang;
    this.translate.use(this.currentLang.value); 
  }
  homeNav(){
    this.store.dispatch(new ResetState()).pipe(takeUntil(this._subject$)).subscribe();
    this.router.navigate(["/dashboard/home"]);
  }
  subscribeToSearchFilter(): void {
    this.sharedService.searchDossierListFilter$
      .pipe(takeUntil(this._subject$))
      .subscribe((filter) => {
        if (filter) {
          this.state = filter;
        }
      });
  }  
  onSearch(): void {

    this.subscribeToSearchFilter();
    var searchFilter: any = {};
  
    if (/^[a-zA-Z]+$/.test(this.dossierFilter)) {
      searchFilter.lastName = this.dossierFilter;
    } else {
      searchFilter.dossierNumber = this.dossierFilter;
    }

    if (this.state.filter.filters.length > 0) {
      this.state.filter.filters = this.state.filter.filters.map(
        (filter: CompositeFilterDescriptor) => {
          if (filter.filters) {

            const oppositeFilterField = searchFilter.lastName ? "dossierNumber" : "lastName";
            filter.filters = filter.filters.filter((subFilter: FilterDescriptor) => subFilter.field !== oppositeFilterField);
            const currentFilterField = searchFilter.lastName ? "lastName" : "dossierNumber";
            const currentFilterIndex = filter.filters.findIndex((subFilter: FilterDescriptor) => subFilter.field === currentFilterField);
            if (currentFilterIndex !== -1) {
              filter.filters[currentFilterIndex] = { field: currentFilterField, operator: "contains", value: searchFilter[currentFilterField] };
            }
          }
          return filter;
        }
      );
    }

    Object.entries(searchFilter).forEach(([key, value]) => {
      if (value) {
        const filter = {
          field: key,
          operator: "contains",
          value: value,
        };
        this.state.filter.filters.push({
          logic: "and",
          filters: [filter],
        });
      }
    });
    this.sharedService.setSearchFilter(this.state);
    this.sharedService.setMainRoute(this.router.url);
    this.router.navigate(["/dossier/dossierList"]);
    this.dossierFilter=""
  }
  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach(s => s.unsubscribe());
    }
  }
}